import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { uploadFile } from '@/shared/api';
import { api, unwrap } from '@/shared/api/client';
import { idempotent } from '@/shared/api/headers';

import { DEALS_ROOT_KEY, customerKeys } from './keys';

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'CustomerCreate'>) => unwrap(api.POST('/customers', { body, params: { header: idempotent() } })),
    onSuccess: (customer) => saved(qc, customer),
  });
}

/** Audited reveal of one PII field. */
export function revealCustomerField(customerId: string, field: 'emiratesId' | 'mobile' | 'passportNumber') {
  return unwrap(api.POST('/customers/{customerId}/reveal', { params: { path: { customerId } }, body: { field } })).then((r) => r.value);
}

/** Records the customer's relationship with a bank (CIF, account, status); reused by later deals with that bank. */
export function useUpsertBankRelationship(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, body }: { bankId: string; body: Schema<'CustomerBankRelationshipInput'> }) => unwrap(api.PUT('/customers/{customerId}/bank-relationships/{bankId}', { params: { path: { customerId, bankId } }, body })),
    onSuccess: (customer) => {
      saved(qc, customer);
      // Deal headers show the CIF for the deal's bank.
      void qc.invalidateQueries({ queryKey: DEALS_ROOT_KEY });
    },
  });
}

/** Sets the returned record into the detail cache and refreshes what depends on it. */
function saved(qc: QueryClient, customer: Schema<'Customer'>) {
  qc.setQueryData(customerKeys.detail(customer.id), customer);
  void qc.invalidateQueries({ queryKey: customerKeys.activity(customer.id) });
  void qc.invalidateQueries({ queryKey: customerKeys.lists() });
}

/** Identity and contact edit. Versioned: sends If-Match; a 409 opens the conflict dialog. */
export function useUpdateCustomer(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, body }: { version: string; body: Schema<'CustomerUpdate'> }) => unwrap(api.PATCH('/customers/{customerId}', { params: { path: { customerId }, header: { 'If-Match': version } }, body })),
    onSuccess: (customer) => {
      saved(qc, customer);
      // Deal headers and snapshots show the customer's name.
      void qc.invalidateQueries({ queryKey: DEALS_ROOT_KEY });
    },
  });
}

/** Asks for a correction of protected identity fields (B1): stored on the customer, audited and notified. */
export function useRequestCorrection(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'CustomerCorrectionRequestInput'>) => unwrap(api.POST('/customers/{customerId}/correction-requests', { params: { path: { customerId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKeys.corrections(customerId) });
      void qc.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
      void qc.invalidateQueries({ queryKey: customerKeys.activity(customerId) });
    },
  });
}

/** Marks an open correction request done or dismissed (customer.identity.edit). */
export function useResolveCorrection(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: Schema<'CustomerCorrectionResolve'> }) => unwrap(api.POST('/customers/{customerId}/correction-requests/{requestId}/resolve', { params: { path: { customerId, requestId } }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKeys.corrections(customerId) });
      void qc.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
      void qc.invalidateQueries({ queryKey: customerKeys.activity(customerId) });
    },
  });
}

export function useAddEmployment(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'EmploymentInput'>) => unwrap(api.POST('/customers/{customerId}/employments', { params: { path: { customerId } }, body })),
    onSuccess: (customer) => saved(qc, customer),
  });
}

export function useEndEmployment(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employmentId, body }: { employmentId: string; body: Schema<'EmploymentEnd'> }) => unwrap(api.POST('/customers/{customerId}/employments/{employmentId}/end', { params: { path: { customerId, employmentId } }, body })),
    onSuccess: (customer) => saved(qc, customer),
  });
}

export function useEndBankRelationship(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bankId, body }: { bankId: string; body: Schema<'CustomerBankRelationshipEnd'> }) => unwrap(api.POST('/customers/{customerId}/bank-relationships/{bankId}/end', { params: { path: { customerId, bankId } }, body })),
    onSuccess: (customer) => {
      saved(qc, customer);
      void qc.invalidateQueries({ queryKey: DEALS_ROOT_KEY });
    },
  });
}

/** Uploads the file to storage, then adds or replaces the customer document. */
export function useUploadCustomerDocument(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, body, onProgress }: { file: File; body: Omit<Schema<'CustomerDocumentInput'>, 'uploadId'>; onProgress: (p: number) => void }) => {
      const uploadId = await uploadFile(file, onProgress);
      return unwrap(api.POST('/customers/{customerId}/documents', { params: { path: { customerId } }, body: { ...body, uploadId } }));
    },
    onSuccess: (customer) => {
      saved(qc, customer);
      void qc.invalidateQueries({ queryKey: [...customerKeys.detail(customerId), 'documents'] });
    },
  });
}

/** Merges a duplicate pair. Deals move between records, so deal lists and both customer records refresh. */
export function useMergeCustomers(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'CustomerMergeInput'>) => unwrap(api.POST('/customers/{customerId}/merge', { params: { path: { customerId }, header: idempotent() }, body })),
    onSuccess: (result) => {
      saved(qc, result.survivor);
      void qc.invalidateQueries({ queryKey: customerKeys.detail(result.mergedId) });
      void qc.invalidateQueries({ queryKey: customerKeys.duplicates(customerId) });
      void qc.invalidateQueries({ queryKey: DEALS_ROOT_KEY });
    },
  });
}
